import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  HStack,
  VStack,
  Text,
  BoxProps,
  FormControl,
  FormLabel,
  Switch,
} from "@chakra-ui/react";
import React from "react";
import { TextField } from "../fields/TextField/TextField";
import { Button, IconButton } from "@chakra-ui/button";
import { PlusIcon } from "../../styles/icons";

export interface ThirdPartyDataFieldDataletConfiguration {
  ThirdPartyIdentifier?: string | null;
  JsonPath?: string | null;
  OverrideExistingFieldDataletValue?: boolean;
  AddToExistingFieldDataletValue?: boolean;
  IsRequired?: boolean;
  IsReadOnlyForPatron?: boolean;
}

interface ThirdPartyDataConfigSectionProps {
  fieldName: string;
  configurations: ThirdPartyDataFieldDataletConfiguration[] | null | undefined;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  isDisabled?: boolean;
}

export const ThirdPartyDataConfigSection = ({
  fieldName,
  configurations,
  setFieldValue,
  isDisabled = false,
  ...props
}: ThirdPartyDataConfigSectionProps & BoxProps) => {
  const addConfiguration = () => {
    const newConfig: ThirdPartyDataFieldDataletConfiguration = {
      ThirdPartyIdentifier: "*",
      JsonPath: "",
      OverrideExistingFieldDataletValue: false,
      AddToExistingFieldDataletValue: false,
      IsRequired: false,
      IsReadOnlyForPatron: false,
    };

    if (configurations) {
      setFieldValue(fieldName, [...configurations, newConfig]);
    } else {
      setFieldValue(fieldName, [newConfig]);
    }
  };

  const deleteConfiguration = (configIdx: number) => {
    if (configurations) {
      const updated = [...configurations];
      updated.splice(configIdx, 1);
      setFieldValue(fieldName, updated);
    }
  };

  const handleOverrideChange = (configIdx: number, newValue: boolean) => {
    setFieldValue(
      `${fieldName}[${configIdx}].OverrideExistingFieldDataletValue`,
      newValue
    );
    if (newValue) {
      // When Override is turned on, turn off AddTo
      setFieldValue(
        `${fieldName}[${configIdx}].AddToExistingFieldDataletValue`,
        false
      );
    }
  };

  const handleAddToChange = (configIdx: number, newValue: boolean) => {
    setFieldValue(
      `${fieldName}[${configIdx}].AddToExistingFieldDataletValue`,
      newValue
    );
    if (newValue) {
      // When AddTo is turned on, turn off Override
      setFieldValue(
        `${fieldName}[${configIdx}].OverrideExistingFieldDataletValue`,
        false
      );
    }
  };

  const handleBooleanChange = (
    configIdx: number,
    field: string,
    newValue: boolean
  ) => {
    setFieldValue(`${fieldName}[${configIdx}].${field}`, newValue);
  };

  return (
    <Box
      w="100%"
      p={4}
      border="1px solid"
      borderColor="cherryButtonDark.50"
      borderRadius="md"
      {...props}
    >
      <HStack w="100%">
        <Text flexGrow={2} fontSize="sm">
          <b>Third Party Data Configurations</b> :
        </Text>
        <Button
          size="xs"
          leftIcon={<PlusIcon />}
          onClick={addConfiguration}
          colorScheme="cherryButton"
          isDisabled={isDisabled}
        >
          New Configuration
        </Button>
      </HStack>

      <Divider mb={2} mt={1} />
      <VStack w="100%" spacing={4}>
        {(configurations?.length &&
          configurations?.map((config, configIdx) => {
            return (
              <HStack
                key={configIdx}
                w="100%"
                p={3}
                border="1px solid"
                borderColor="cherryGrey.200"
                borderRadius="md"
                bgColor="white"
                alignItems="center"
                spacing={4}
              >
                <VStack w="100%" spacing={3} flex={1}>
                  <TextField
                    horizontal
                    label="Third Party Identifier"
                    name={`${fieldName}[${configIdx}].ThirdPartyIdentifier`}
                    isDisabled={isDisabled}
                  />
                  <TextField
                    horizontal
                    label="Json Path"
                    name={`${fieldName}[${configIdx}].JsonPath`}
                    isDisabled={isDisabled}
                  />

                  <HStack w="100%" spacing={4}>
                    <FormControl display="flex" flexDir="row">
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        flex={1}
                        pt={2}
                        flexShrink={1}
                      >
                        <FormLabel
                          fontWeight="bold"
                          color="cherryGrey.900"
                          whiteSpace="nowrap"
                        >
                          Override Existing Value
                        </FormLabel>
                      </VStack>
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        alignSelf="center"
                        flexGrow={1}
                      >
                        <Switch
                          colorScheme="green"
                          isChecked={
                            config.OverrideExistingFieldDataletValue ?? false
                          }
                          onChange={(e) =>
                            handleOverrideChange(configIdx, e.target.checked)
                          }
                          isDisabled={isDisabled}
                        />
                      </VStack>
                    </FormControl>

                    <FormControl display="flex" flexDir="row">
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        flex={1}
                        pt={2}
                        flexShrink={1}
                      >
                        <FormLabel
                          fontWeight="bold"
                          color="cherryGrey.900"
                          whiteSpace="nowrap"
                        >
                          Add to Existing Value
                        </FormLabel>
                      </VStack>
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        alignSelf="center"
                        flexGrow={1}
                      >
                        <Switch
                          colorScheme="green"
                          isChecked={
                            config.AddToExistingFieldDataletValue ?? false
                          }
                          onChange={(e) =>
                            handleAddToChange(configIdx, e.target.checked)
                          }
                          isDisabled={isDisabled}
                        />
                      </VStack>
                    </FormControl>

                    <FormControl display="flex" flexDir="row">
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        flex={1}
                        pt={2}
                        flexShrink={1}
                      >
                        <FormLabel
                          fontWeight="bold"
                          color="cherryGrey.900"
                          whiteSpace="nowrap"
                        >
                          Is Required
                        </FormLabel>
                      </VStack>
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        alignSelf="center"
                        flexGrow={1}
                      >
                        <Switch
                          colorScheme="green"
                          isChecked={config.IsRequired ?? false}
                          onChange={(e) =>
                            handleBooleanChange(
                              configIdx,
                              "IsRequired",
                              e.target.checked
                            )
                          }
                          isDisabled={isDisabled}
                        />
                      </VStack>
                    </FormControl>

                    <FormControl display="flex" flexDir="row">
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        flex={1}
                        pt={2}
                        flexShrink={1}
                      >
                        <FormLabel
                          fontWeight="bold"
                          color="cherryGrey.900"
                          whiteSpace="nowrap"
                        >
                          Read-only for Patron
                        </FormLabel>
                      </VStack>
                      <VStack
                        w="100%"
                        alignItems="flex-start"
                        alignSelf="center"
                        flexGrow={1}
                      >
                        <Switch
                          colorScheme="green"
                          isChecked={config.IsReadOnlyForPatron ?? false}
                          onChange={(e) =>
                            handleBooleanChange(
                              configIdx,
                              "IsReadOnlyForPatron",
                              e.target.checked
                            )
                          }
                          isDisabled={isDisabled}
                        />
                      </VStack>
                    </FormControl>
                  </HStack>
                </VStack>

                <IconButton
                  aria-label="Delete configuration."
                  title="Delete configuration."
                  icon={<DeleteIcon />}
                  size="sm"
                  onClick={() => deleteConfiguration(configIdx)}
                  isDisabled={isDisabled}
                  alignSelf="center"
                />
              </HStack>
            );
          })) || (
          <Text fontStyle="italic" fontSize="sm">
            No third party data configurations set.
          </Text>
        )}
      </VStack>
    </Box>
  );
};
